{
  const ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const REVISION = '2024-10-15';

  const MESSAGE_CLASS = {
    success: 'message message-success text-success inline-flex',
    error: 'message message-danger text-danger inline-flex'
  };

  let detectedCompanyId = null;

  /*
   * The Klaviyo app embed loads static.klaviyo.com/onsite/js/<company_id>/klaviyo.js,
   * so the public API key is already on the page. Reading it here means the merchant
   * only has to fill in the list ID in the theme editor.
   */
  const detectCompanyId = () => {
    if (detectedCompanyId !== null) return detectedCompanyId;

    detectedCompanyId = '';

    const scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite/js/"]');
    for (const script of scripts) {
      const src = script.src || '';
      const fromQuery = src.match(/[?&]company_id=([A-Za-z0-9]+)/);
      if (fromQuery) {
        detectedCompanyId = fromQuery[1];
        break;
      }

      const fromPath = src.match(/\/onsite\/js\/([A-Za-z0-9]+)\/klaviyo\.js/);
      if (fromPath) {
        detectedCompanyId = fromPath[1];
        break;
      }
    }

    return detectedCompanyId;
  };

  const setMessage = (form, text, state) => {
    const box = form.querySelector('.klaviyo-newsletter__message');
    if (!box) return;

    box.textContent = '';
    if (!text) return;

    const note = document.createElement('div');
    note.className = MESSAGE_CLASS[state] || MESSAGE_CLASS.error;
    note.textContent = text;
    box.appendChild(note);
  };

  const setBusy = (form, busy) => {
    const button = form.querySelector('button[type="submit"]');
    if (!button) return;

    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  const subscribe = async (form) => {
    const input = form.querySelector('input[type="email"]');
    const email = input ? input.value.trim() : '';
    const companyId = form.dataset.klaviyoCompanyId || detectCompanyId();
    const listId = form.dataset.klaviyoListId;

    if (!email || !input.checkValidity()) {
      setMessage(form, form.dataset.errorText, 'error');
      if (input) input.focus();
      return;
    }

    if (!companyId) {
      setMessage(form, form.dataset.errorText, 'error');
      console.warn(
        '[klaviyo-newsletter] Klaviyo public API key not found. Enable the Klaviyo app embed or fill in the key in the theme editor.'
      );
      return;
    }

    if (!listId) {
      setMessage(form, form.dataset.errorText, 'error');
      console.warn('[klaviyo-newsletter] Klaviyo list ID is missing in the theme editor.');
      return;
    }

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: {
            data: { type: 'list', id: listId }
          }
        }
      }
    };

    if (form.dataset.klaviyoSource) {
      payload.data.attributes.custom_source = form.dataset.klaviyoSource;
    }

    setMessage(form, '', 'success');
    setBusy(form, true);

    try {
      const response = await fetch(`${ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: REVISION
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Klaviyo responded with status ${response.status}`);
      }

      setMessage(form, form.dataset.successText, 'success');
      form.reset();

      try {
        localStorage.setItem('essence:newsletter-subscribed', true);
      } catch (storageError) {
        console.warn('[klaviyo-newsletter] localStorage unavailable', storageError);
      }

      document.dispatchEvent(
        new CustomEvent('klaviyo-newsletter:subscribed', {
          detail: { email: email, listId: listId }
        })
      );
    } catch (error) {
      console.error('[klaviyo-newsletter]', error);
      setMessage(form, form.dataset.errorText, 'error');
    } finally {
      setBusy(form, false);
    }
  };

  document.addEventListener('submit', (event) => {
    const target = event.target;
    const form = target && target.closest ? target.closest('form.klaviyo-newsletter') : null;
    if (!form) return;

    event.preventDefault();
    subscribe(form);
  });
}
